import { Head, Link, usePage } from "@inertiajs/react"
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle,
  Clock,
  DollarSign,
  Globe,
  Shield,
  Zap,
} from "lucide-react"

import AppLogo from "@/components/app-logo"
import { dashboardPath, signInPath } from "@/routes"
import type { SharedData } from "@/types"

export default function Welcome() {
  const page = usePage<SharedData>()
  const { auth } = page.props

  return (
    <>
      <Head title="ProxyOps - AI-Native Proxy Rotation & Observability">
        <meta
          name="description"
          content="The proxy rotation platform built for AI agents. Automatic failover, per-domain ban prevention, and cost optimization. No proxy expertise required."
        />
        <link rel="preconnect" href="https://fonts.bunny.net" />
        <link
          href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600,700"
          rel="stylesheet"
        />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
        {/* Navigation */}
        <header className="relative z-50 w-full border-b border-slate-200/50 bg-white/80 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center space-x-3">
                <AppLogo />
              </div>

              <nav className="hidden items-center space-x-8 md:flex">
                <a
                  href="#features"
                  className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                >
                  Pricing
                </a>
                <a
                  href="#docs"
                  className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                >
                  Docs
                </a>
              </nav>

              <div className="flex items-center space-x-4">
                {auth.user ? (
                  <Link
                    href={dashboardPath()}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href={signInPath()}
                      className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                    >
                      Sign In
                    </Link>
                    <Link
                      href={signInPath()}
                      className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                    >
                      Start Free Trial
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 sm:py-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              <div className="sm:text-center md:mx-auto md:max-w-2xl lg:col-span-6 lg:text-left">
                <div className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                  <Bot className="mr-2 h-4 w-4" />
                  Built for AI Agents
                </div>

                <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-6xl dark:text-white">
                  Focus on AI,{" "}
                  <span className="text-blue-600">not IP babysitting</span>
                </h1>

                <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
                  The proxy rotation platform built for AI agents. Automatic
                  failover, per-domain ban prevention, and cost optimization. No
                  proxy expertise required.
                </p>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <Link
                    href={signInPath()}
                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700"
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  <a
                    href="#demo"
                    className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-6 py-3 text-base font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Watch Demo
                  </a>
                </div>

                <div className="mt-8 flex items-center space-x-6 text-sm text-slate-500 dark:text-slate-400">
                  <div className="flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                    Free 50K requests/month
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                    No setup required
                  </div>
                </div>
              </div>

              <div className="relative mt-12 lg:col-span-6 lg:mt-0">
                <div className="relative mx-auto w-full max-w-lg">
                  <div className="rounded-2xl bg-gradient-to-tr from-blue-600 via-purple-600 to-pink-600 p-8 shadow-2xl">
                    <div className="space-y-4">
                      <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">
                            Agent Request
                          </span>
                          <span className="text-xs text-green-200">
                            ✓ Success
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-white/80">
                          youtube.com → IP Pool A
                        </div>
                      </div>

                      <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">
                            Auto Failover
                          </span>
                          <span className="text-xs text-yellow-200">
                            → Switched
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-white/80">
                          reddit.com → IP Pool B (A banned)
                        </div>
                      </div>

                      <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">
                            Cost Optimized
                          </span>
                          <span className="text-xs text-green-200">
                            -30% savings
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-white/80">
                          Reusing healthy IPs
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="bg-slate-50 py-16 dark:bg-slate-900/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                AI agents keep hitting the same proxy walls
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
                Every framework punts proxy rotation to developers. We solve it
                once and for all.
              </p>
            </div>

            <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  icon: AlertTriangle,
                  title: "Bot Detection",
                  description:
                    "LLM data gathering trips bot detection on YouTube, Reddit, TikTok",
                },
                {
                  icon: Clock,
                  title: "Latency Spikes",
                  description:
                    "Slow proxies stall agent chains and produce false 'site is slow' alerts",
                },
                {
                  icon: DollarSign,
                  title: "Proxy Burn Rate",
                  description:
                    "One bad scrape poisons entire IP pools, driving up costs",
                },
                {
                  icon: Zap,
                  title: "Manual Rotation",
                  description:
                    "DIY rotation logic bloats tools code and slows down teams",
                },
              ].map((problem, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-white p-6 shadow-sm dark:bg-slate-800"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20">
                    <problem.icon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
                    {problem.title}
                  </h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-400">
                    {problem.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                The proxy layer AI agents deserve
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
                Per-domain health ledgers, automatic failover, and cost
                optimization built for modern AI workloads.
              </p>
            </div>

            <div className="mt-16 grid gap-8 lg:grid-cols-3">
              {[
                {
                  icon: Shield,
                  title: "Per-Domain Ban Prevention",
                  description:
                    "One scrape to YouTube fails? That IP is blocked only for YouTube—still useful for TikTok. Smart isolation prevents cascade failures.",
                  highlight: "30% fewer burned IPs",
                },
                {
                  icon: Zap,
                  title: "Automatic Failover",
                  description:
                    "Agent got blocked? We auto-retry on a fresh IP before your chain even throws an error. Zero downtime, zero manual intervention.",
                  highlight: "99.9% uptime SLA",
                },
                {
                  icon: BarChart3,
                  title: "Latency Governance",
                  description:
                    "Slow nodes are sidelined before they stall your job. Real-time p95 alerts and automatic de-rating of underperforming IPs.",
                  highlight: "40% faster avg response",
                },
                {
                  icon: Globe,
                  title: "Multi-Platform Health",
                  description:
                    "Track ban rates, success rates, and latency per platform. Webhook invalidation API lets your app tell us when a proxy is burnt.",
                  highlight: "Real-time observability",
                },
                {
                  icon: DollarSign,
                  title: "Cost Optimization",
                  description:
                    "Use 30% fewer proxies through intelligent reuse of healthy IPs. Algorithm chooses cheapest viable proxy class based on historical ban rates.",
                  highlight: "30% cost reduction",
                },
                {
                  icon: Bot,
                  title: "Agent-Native Integration",
                  description:
                    "Drop-in SDK for LangChain, CrewAI, Autogen. MCP-compliant gateway. Flowise plugin. Works with any agent framework.",
                  highlight: "5-minute setup",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20">
                    <feature.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-slate-900 dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="mt-4 text-slate-600 dark:text-slate-400">
                    {feature.description}
                  </p>
                  <div className="mt-4 inline-flex rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900/20 dark:text-green-400">
                    {feature.highlight}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="bg-slate-50 py-16 dark:bg-slate-900/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                Built for the AI era
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
                Whether you&apos;re building agents, training models, or
                automating data collection.
              </p>
            </div>

            <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  title: "LLM Data Pipelines",
                  description:
                    "Continuous scraping for fine-tuning and RAG indexing. Automatic ban detection keeps your collectors online and predictable.",
                  use_case: "YouTube, Reddit, TikTok data gathering",
                },
                {
                  title: "Agent Frameworks",
                  description:
                    "Drop-in HTTP endpoint for CrewAI, LangChain, Autogen. Simple feedback hook lets agents tell you what failed.",
                  use_case: "Autonomous web browsing & tool execution",
                },
                {
                  title: "E-commerce Intelligence",
                  description:
                    "Reliable hourly price snapshots across regions. Per-platform health metrics prevent stale pricing data.",
                  use_case: "MAP violation tracking, competitor monitoring",
                },
              ].map((useCase, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-white p-6 shadow-sm dark:bg-slate-800"
                >
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {useCase.title}
                  </h3>
                  <p className="mt-3 text-slate-600 dark:text-slate-400">
                    {useCase.description}
                  </p>
                  <div className="mt-4 text-sm font-medium text-blue-600 dark:text-blue-400">
                    {useCase.use_case}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
              Ready to stop babysitting proxies?
            </h2>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              Join the developers building the next generation of AI agents.
              Free tier includes 50K requests per month.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Link
                href={signInPath()}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-8 py-4 text-lg font-medium text-white shadow-sm hover:bg-blue-700"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#docs"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-8 py-4 text-lg font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                View Documentation
              </a>
            </div>

            <div className="mt-8 flex items-center justify-center space-x-6 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center">
                <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                5-minute setup
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                Cancel anytime
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AppLogo />
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                © 2024 ProxyOps. Built for the AI era.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
